<template>
  <section class="companies-section">
    <!-- Header -->
    <div class="section-header">
      <div class="header-content">
        <p class="has-text-weight-bold is-size-4">Aplicações</p>
        <p class="is-size-6 text-muted">Faça aqui a gestão dos seus Goldylocks.</p>
      </div>
      <div class="header-actions">
        <a target="_blank" href="https://www.goldylocks.pt/registo/" class="button is-primary"> 
          <i class="fas fa-plus mr-2"></i>
          Criar nova
        </a>
      </div>
    </div>

    <!-- Filters -->
    <div class="filters-bar">
      <div class="search-field">
        <div class="control has-icons-left has-icons-right">
          <input 
            class="input" 
            type="search" 
            placeholder="Pesquisar empresas..." 
            v-model="localSearch"
            @input="handleSearchInput"
          >
          <span class="icon is-small is-left">
            <i class="fas fa-search"></i>
          </span>
          <span 
            class="icon is-small is-right is-clickable" 
            v-if="localSearch" 
            @click="clearSearch"
          >
            <i class="fas fa-times"></i>
          </span>
        </div>
      </div>
      
      <div class="filter-group">
        <div class="select" :class="{'is-loading': isFiltering}">
          <select v-model="state" @change="setFiltering">
            <option value="">Todos os estados</option>
            <option value="active">Ativo</option>
            <option value="near-expire">A expirar (≤30 dias)</option>
            <option value="inactive">Expirado</option>
          </select>
        </div>
        
        <div class="select" :class="{'is-loading': isFiltering}">
          <select v-model="sort" @change="setFiltering">
            <option value="remaining-days-asc">Dias restantes ↑</option>
            <option value="remaining-days-desc">Dias restantes ↓</option>
            <option value="name-asc">Nome A-Z</option>
            <option value="name-desc">Nome Z-A</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Results Info -->
    <div class="results-info" v-if="localSearch || state">
      <p class="has-text-grey is-size-7">
        <span v-if="searchResults.length > 0">
          <strong>{{ searchResults.length }}</strong> empresa{{ searchResults.length !== 1 ? 's' : ''}} encontrada{{ searchResults.length !== 1 ? 's' : ''}}
        </span>
        <span v-else>
          Nenhuma empresa encontrada
        </span>
      </p>
      <button 
        class="button is-small is-ghost" 
        @click="clearAllFilters"
        v-if="localSearch || state"
      >
        <i class="fas fa-times mr-1"></i>
        Limpar filtros
      </button>
    </div>

    <!-- Loading State -->
    <div v-if="isFiltering" class="state-container">
      <div class="skeleton-cards">
        <div class="skeleton-card" v-for="i in 3" :key="i">
          <div class="skeleton-line w-1/3"></div>
          <div class="skeleton-line w-2/3"></div>
          <div class="skeleton-line w-1/2"></div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else-if="!companies || companies.length === 0" class="state-container empty-state">
      <div class="empty-icon">
        <i class="fas fa-building"></i>
      </div>
      <h3>Nenhuma aplicação encontrada</h3>
      <p>Ainda não tem nenhuma aplicação Goldylocks associada.</p>
      <a target="_blank" href="https://www.goldylocks.pt/registo/" class="button is-primary mt-4">
        <i class="fas fa-plus mr-2"></i>
        Criar primeira aplicação
      </a>
    </div>

    <!-- No Results -->
    <div v-else-if="searchResults.length === 0 && (localSearch || state)" class="state-container no-results">
      <div class="empty-icon">
        <i class="fas fa-search"></i>
      </div>
      <h3>Nenhum resultado</h3>
      <p>Não foram encontradas empresas com os filtros atuais.</p>
      <button class="button is-small is-link mt-3" @click="clearAllFilters">Limpar filtros</button>
    </div>

    <!-- Companies List -->
    <div v-else class="companies-list">
      <div class="list-header is-hidden-touch">
        <div class="col-status">Estado</div>
        <div class="col-company">Empresa</div>
        <div class="col-plan">Plano</div>
        <div class="col-expiry">Dias restantes</div>
        <div class="col-actions"></div>
      </div>
      
      <div class="companies-grid">
        <CompaniesRow 
          :company="company" 
          v-for="company of searchResults" 
          :key="company.id_goldylocks"
        ></CompaniesRow>
      </div>
    </div>
  </section>
</template>

<script>
import CompaniesRow from "@/components/dashboard/CompaniesRow";

export default {
  name: "CompaniesTable",
  components: {CompaniesRow},
  props: ['companies'],
  data() {
    return {
      localSearch: '',
      isFiltering: false,
      debounceTimer: null,
      state: ''
    }
  },
  computed: {
    search: {
      get() {
        return this.$store.getters.search
      },
      set(val) {
        this.$store.commit('search', val)
      }
    },

    sort: {
      get() {
        return this.$store.getters.sort
      },
      set(val) {
        this.$store.commit('sort', val)
      }
    },
    state: {
      get() {
        return this.$data.state
      },
      set(val) {
        this.$data.state = val
      }
    },
    searchResults() {
      let filteredCompanies = this.companies ? [...this.companies] : [];

      const getRemainingDays = (company) => {
        const days = company && company.modules && company.modules.vendas && company.modules.vendas.remainingDays;
        return parseInt(days) || 0;
      };

      const searchFields = [
        'nome_goldylocks',
        'nif_goldylocks',
        'alias_goldylocks',
        'agente_email',
        'agente_nome',
        'contabilista_email',
        'contabilista_nome'
      ];

      if (this.search && this.search.length > 0) {
        const searchString = this.search.toLowerCase();
        filteredCompanies = filteredCompanies.filter(company =>
          searchFields.some(field => {
            const value = company[field];
            return value && value.toLowerCase().includes(searchString);
          })
        );
      }

      if (this.state) {
        filteredCompanies = filteredCompanies.filter(company => {
          if (this.state === 'active') {
            return company.active === true;
          } else if (this.state === 'inactive') {
            return company.active === false;
          } else if (this.state === 'near-expire') {
            const remainingDays = getRemainingDays(company);
            return remainingDays > 0 && remainingDays <= 30;
          }
          return true;
        });
      }

      if (this.sort === 'remaining-days-asc') {
        filteredCompanies.sort((a, b) => getRemainingDays(a) - getRemainingDays(b));
      } else if (this.sort === 'remaining-days-desc') {
        filteredCompanies.sort((a, b) => getRemainingDays(b) - getRemainingDays(a));
      } else if (this.sort === 'name-asc') {
        filteredCompanies.sort((a, b) => (a.nome_goldylocks || '').localeCompare(b.nome_goldylocks || ''));
      } else if (this.sort === 'name-desc') {
        filteredCompanies.sort((a, b) => (b.nome_goldylocks || '').localeCompare(a.nome_goldylocks || ''));
      }

      return filteredCompanies;
    }
  },
  methods: {
    handleSearchInput() {
      this.isFiltering = true;
      if (this.debounceTimer) clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.search = this.localSearch;
        this.isFiltering = false;
      }, 300);
    },
    setFiltering() {
      this.isFiltering = true;
      setTimeout(() => {
        this.isFiltering = false;
      }, 150);
    },
    clearSearch() {
      this.localSearch = '';
      this.search = '';
    },
    clearAllFilters() {
      this.localSearch = '';
      this.search = '';
      this.state = '';
      this.sort = 'remaining-days-asc';
    }
  },
  mounted() {
    this.localSearch = this.search;
    this.$store.commit('context', 'usercompanies');
    this.$store.dispatch('fetchProfile');
  }
}
</script>

<style scoped>
.companies-section {
  max-width: 1200px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
}

.header-content p {
  margin-bottom: 0;
}

.header-actions .button {
  font-weight: 600;
}

.filters-bar {
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.search-field {
  flex: 1;
  min-width: 200px;
  max-width: 400px;
}

.search-field .input {
  height: 44px;
}

.filter-group {
  display: flex;
  gap: 12px;
}

.filter-group .select select {
  height: 44px;
  min-width: 180px;
  background: var(--surface);
  border: 1px solid #E2E8F0;
}

.results-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 12px 16px;
  background: var(--surface-elevated);
  border-radius: var(--radius-md);
}

.state-container {
  padding: 60px 20px;
  text-align: center;
}

.empty-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto 24px;
  background: var(--primary-50);
  border-radius: var(--radius-xl);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  color: var(--primary);
}

.empty-state h3, .no-results h3 {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 8px;
}

.empty-state p, .no-results p {
  color: var(--text-secondary);
}

/* Skeleton Loading */
.skeleton-cards {
  display: grid;
  gap: 16px;
}

.skeleton-card {
  background: var(--surface);
  border-radius: var(--radius-lg);
  padding: 20px;
}

.skeleton-line {
  height: 16px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
  margin-bottom: 12px;
}

.skeleton-line:last-child {
  margin-bottom: 0;
  width: 50%;
}

@keyframes shimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}

/* Companies List */
.list-header {
  display: grid;
  grid-template-columns: 120px 1fr 120px 100px 180px;
  gap: 16px;
  padding: 12px 24px;
  background: var(--surface-elevated);
  border-radius: var(--radius-lg);
  font-weight: 600;
  color: var(--text-secondary);
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 12px;
}

.companies-grid {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.col-status { text-align: center; }
.col-company { padding-left: 8px; }
.col-plan { text-align: center; }
.col-expiry { text-align: center; }
.col-actions { text-align: right; }

.is-clickable {
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.is-clickable:hover {
  opacity: 0.7;
}

@media only screen and (max-width: 768px) {
  .filters-bar {
    flex-direction: column;
  }
  
  .search-field {
    max-width: none;
  }
  
  .filter-group {
    width: 100%;
  }
  
  .filter-group .select select {
    flex: 1;
  }
}
</style>
